import * as React from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  apiClient,
  ApiError,
  type Enrolment,
  type FormField,
  type RecordEdit,
} from "@alight/contracts";
import {
  Button,
  DateOfBirthInput,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@alight/ui";
import { AlertTriangle, History } from "lucide-react";
import { isBirthDateField } from "../form-field";

/**
 * CORRECT a submitted enrolment.
 *
 * An enrolment is a snapshot of what a family typed on the day, so this is
 * deliberately a correction tool, not a re-submission: it edits the names, the
 * email, the year level and the form answers, and nothing else. Payment
 * reference, amount, payment status and the program are all fixed here — those
 * are settled by the payment and reconciliation screens, and a text box is the
 * wrong instrument for them (see handlers/adminCorrections.ts).
 *
 * THE EMAIL IS NOT PART OF THE SNAPSHOT. Every other box here rewrites this one
 * registration; the email is the family's sign-in identity, so it re-keys their
 * whole household in one go through a separate call. It is offered anyway,
 * because a mistyped address is discovered HERE — reading the registration that
 * bounced — and sending the host to another screen to fix it just meant the
 * typo got left alone.
 *
 * The answer list is driven by the PROGRAM'S FORM, not just by what happens to
 * be stored, so a question the family skipped (or that was added to the form
 * after they enrolled) can still be filled in — which is the common case for
 * the details a host actually chases, like an emergency contact.
 *
 * Because a correction rewrites what a family submitted — including their
 * consents — every change is recorded, and the history is shown right here.
 */

/** Keys that are provenance/plumbing rather than something a family answered. */
const INTERNAL_KEYS = new Set([
  "source",
  "import_id",
  "added_by",
  "turnstileToken",
]);

/** Journey keys with no form-field definition but which are still real answers. */
const JOURNEY_LABELS: Record<string, string> = {
  cash_when: "Cash handover",
  already_note: "How they paid",
};

/** One editable answer: a form field where we have its definition, or a bare
 *  key recovered from what was submitted. */
interface AnswerField {
  name: string;
  label: string;
  type: FormField["type"];
  options: string[];
  /** True when the field is no longer on the live form but has an answer. */
  legacy: boolean;
}

export function humanizeKey(key: string): string {
  if (JOURNEY_LABELS[key]) return JOURNEY_LABELS[key];
  return key
    .replace(/[_-]+/g, " ")
    .replace(/^\w|\s\w/g, (c) => c.toUpperCase())
    .trim();
}

/**
 * Merge the program's live form with whatever the submission actually holds.
 * Live fields come first in their form order (so the edit screen reads like the
 * form the parent filled in); anything answered but no longer on the form is
 * appended, flagged legacy, so an old answer is never silently un-editable.
 */
export function buildAnswerFields(
  formFields: FormField[],
  extra: Record<string, unknown>,
): AnswerField[] {
  const live = formFields
    .filter((f) => f.archived === 0)
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order || a.id - b.id)
    .map<AnswerField>((f) => ({
      name: f.name,
      label: f.label,
      type: f.type,
      options: f.options ?? [],
      legacy: false,
    }));
  const seen = new Set(live.map((f) => f.name));

  const leftovers = Object.keys(extra)
    .filter((key) => !seen.has(key) && !INTERNAL_KEYS.has(key))
    .map<AnswerField>((key) => ({
      name: key,
      label: humanizeKey(key),
      // A stored boolean is a tick box; everything else edits as text.
      type: typeof extra[key] === "boolean" ? "checkbox" : "text",
      options: [],
      legacy: true,
    }));

  return [...live, ...leftovers];
}

/** Stored value -> the string an <input> holds. Booleans are handled separately. */
function toInputValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

export function EditEnrolmentDialog({
  api,
  enrolment,
  programLabel,
  formFields,
  onClose,
  onMoveInstead,
}: {
  api: ReturnType<typeof apiClient>;
  enrolment: Enrolment | null;
  programLabel: string;
  formFields: FormField[];
  onClose: () => void;
  /** Hand this registration to the MOVE screen instead. Offered when the new
   *  address already belongs to someone — a re-key cannot serve that case, and
   *  before this the host was simply told to do it by hand, which they cannot. */
  onMoveInstead?: (enrolment: Enrolment) => void;
}) {
  const qc = useQueryClient();
  const extra = React.useMemo(() => enrolment?.extra ?? {}, [enrolment]);
  const answerFields = React.useMemo(
    () => (enrolment ? buildAnswerFields(formFields, extra) : []),
    [enrolment, formFields, extra],
  );

  const [parentName, setParentName] = React.useState(enrolment?.parent_name ?? "");
  const [parentPhone, setParentPhone] = React.useState(enrolment?.parent_phone ?? "");
  const originalEmail = enrolment?.parent_email ?? "";
  const [parentEmail, setParentEmail] = React.useState(originalEmail);
  const [childName, setChildName] = React.useState(enrolment?.child_name ?? "");
  const [yearLevel, setYearLevel] = React.useState(enrolment?.year_level ?? "");
  const [answers, setAnswers] = React.useState<Record<string, unknown>>(() => ({ ...extra }));

  function setAnswer(name: string, value: unknown) {
    setAnswers((prev) => ({ ...prev, [name]: value }));
  }

  const save = useMutation({
    mutationFn: async () => {
      /* The email first, and on its own. It re-keys the family's children and
         enrolments, and the server REFUSES it (409) when the new address
         already belongs to someone else — so if that is going to fail it must
         fail before the rest of the correction is written, not after. The
         enrolment's id survives the re-key, so the PATCH below still lands. */
      if (emailChanged) {
        await api.adminUpdateParent(originalEmail, { email: parentEmail.trim().toLowerCase() });
      }
      // Send every answer the screen showed. Untouched ones are identical to
      // what is stored, and the server diffs before recording, so nothing
      // spurious lands in the history.
      const nextExtra: Record<string, unknown> = {};
      for (const field of answerFields) {
        nextExtra[field.name] =
          field.type === "checkbox" ? Boolean(answers[field.name]) : (answers[field.name] ?? "");
      }
      return api.adminUpdateEnrolment(enrolment!.id, {
        parent_name: parentName.trim(),
        parent_phone: parentPhone.trim() === "" ? null : parentPhone.trim(),
        child_name: childName.trim(),
        year_level: yearLevel.trim(),
        extra: nextExtra,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "enrolments"] });
      // A corrected name/year level is pushed onto the child record too.
      qc.invalidateQueries({ queryKey: ["admin", "children"] });
      qc.invalidateQueries({ queryKey: ["admin", "att"] });
      onClose();
    },
  });

  const emailChanged = parentEmail.trim().toLowerCase() !== originalEmail.toLowerCase();
  const canSave =
    parentName.trim() !== "" && childName.trim() !== "" && parentEmail.trim() !== "";

  return (
    <Dialog open={enrolment !== null} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Correct {enrolment?.payment_ref}</DialogTitle>
          <DialogDescription>
            Fix what was recorded for {enrolment?.child_name} on {programLabel}. The fee, payment
            reference and payment status aren&apos;t changed here — those live on the
            Reconciliation screen. Nothing is emailed to the family, and every change is kept in
            the record&apos;s history.
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
          className="space-y-5"
        >
          <fieldset className="space-y-3">
            <legend className="text-xs font-semibold uppercase tracking-wide text-muted">
              Who this is for
            </legend>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="corr-child">Child&apos;s name</Label>
                <Input
                  id="corr-child"
                  value={childName}
                  onChange={(e) => setChildName(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="corr-year">Year level</Label>
                <Input
                  id="corr-year"
                  value={yearLevel}
                  onChange={(e) => setYearLevel(e.target.value)}
                  placeholder="e.g. Year 4 — leave blank for holiday programs"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="corr-parent">Parent / guardian name</Label>
                <Input
                  id="corr-parent"
                  value={parentName}
                  onChange={(e) => setParentName(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="corr-phone">Phone</Label>
                <Input
                  id="corr-phone"
                  type="tel"
                  value={parentPhone}
                  onChange={(e) => setParentPhone(e.target.value)}
                />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="corr-email">Parent&apos;s email address</Label>
                <Input
                  id="corr-email"
                  type="email"
                  value={parentEmail}
                  onChange={(e) => setParentEmail(e.target.value)}
                  required
                />
              </div>
            </div>
            {emailChanged ? (
              <div
                role="note"
                className="flex items-start gap-2 rounded-card border border-accent bg-accent/10 px-3 py-2 text-xs text-ink"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent-fg" aria-hidden />
                <div className="space-y-1">
                  <p className="font-semibold">Please double-check this address.</p>
                  <p>
                    The email belongs to the whole family, not just this registration. Saving moves
                    every child and every enrolment under {originalEmail || "the old address"} onto
                    the new one, it becomes how they sign in, and the old address stops working
                    straight away.
                  </p>
                  {onMoveInstead && enrolment ? (
                    <p>
                      Just this one child in the wrong place?{" "}
                      <button
                        type="button"
                        className="font-semibold underline underline-offset-2"
                        onClick={() => onMoveInstead(enrolment)}
                      >
                        Move this registration instead
                      </button>{" "}
                      — that leaves the rest of the family alone and asks the new address to
                      confirm.
                    </p>
                  ) : null}
                </div>
              </div>
            ) : null}
            <p className="text-xs text-muted">
              Correcting the child&apos;s name or year level here updates their record on the
              attendance roll too. The name and phone apply to this registration; the email applies
              to the whole family.
            </p>
          </fieldset>

          {answerFields.length > 0 ? (
            <fieldset className="space-y-3">
              <legend className="text-xs font-semibold uppercase tracking-wide text-muted">
                Form answers
              </legend>
              <div className="space-y-3">
                {answerFields.map((field) => (
                  <AnswerInput
                    key={field.name}
                    field={field}
                    value={answers[field.name]}
                    onChange={(v) => setAnswer(field.name, v)}
                  />
                ))}
              </div>
            </fieldset>
          ) : null}

          <div
            role="note"
            className="flex items-start gap-2 rounded-card border border-border bg-paper px-3 py-2 text-xs text-muted"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden />
            <p>
              This rewrites what the family submitted, including their consents. Every change is
              recorded below with the old value, so a correction can always be traced.
            </p>
          </div>

          {save.error ? (
            <div className="space-y-2" role="alert">
              <p className="text-sm text-accent-fg">
                {save.error instanceof ApiError
                  ? save.error.message
                  : "Couldn’t save — please retry."}
              </p>
              {/* 409 = the new address already belongs to a family here. That is
                  not a dead end any more: it is exactly what a MOVE is for. */}
              {save.error instanceof ApiError &&
              save.error.status === 409 &&
              onMoveInstead &&
              enrolment ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onMoveInstead(enrolment)}
                >
                  Move this registration to that address instead
                </Button>
              ) : null}
            </div>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={save.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending || !canSave}>
              {save.isPending
                ? "Saving…"
                : emailChanged
                  ? "Save and move their records"
                  : "Save corrections"}
            </Button>
          </DialogFooter>
        </form>

        {enrolment ? <EditHistory api={api} entity="enrolment" entityId={enrolment.id} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function AnswerInput({
  field,
  value,
  onChange,
}: {
  field: AnswerField;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const id = `corr-extra-${field.name}`;

  if (field.type === "checkbox") {
    return (
      <label className="flex items-start gap-2 text-sm text-ink" htmlFor={id}>
        <input
          id={id}
          type="checkbox"
          className="mt-0.5"
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span>
          {field.label}
          {field.legacy ? <LegacyTag /> : null}
        </span>
      </label>
    );
  }

  const stringValue = toInputValue(value);

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>
        {field.label}
        {field.legacy ? <LegacyTag /> : null}
      </Label>
      {field.type === "textarea" ? (
        <Textarea
          id={id}
          rows={3}
          value={stringValue}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : field.type === "select" && field.options.length > 0 ? (
        <Select value={stringValue} onValueChange={(v) => onChange(v)}>
          <SelectTrigger id={id}>
            <SelectValue placeholder="Not answered" />
          </SelectTrigger>
          <SelectContent>
            {field.options.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : field.type === "date" && isBirthDateField(field) ? (
        <DateOfBirthInput
          id={id}
          label={field.label}
          value={stringValue}
          onChange={(v) => onChange(v)}
        />
      ) : (
        <Input
          id={id}
          type={
            field.type === "email" || field.type === "tel" || field.type === "date"
              ? field.type
              : field.type === "number"
                ? "number"
                : "text"
          }
          value={stringValue}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Not answered"
        />
      )}
    </div>
  );
}

/** Marks an answer whose question is no longer on the live form — it still has
 *  to be editable, but the host should know why it isn't on the form any more. */
function LegacyTag() {
  return (
    <span className="ml-2 rounded-full bg-paper px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide text-muted">
      no longer asked
    </span>
  );
}

/**
 * The correction trail for one record — what changed, from what to what, and
 * who changed it. Silent when there is nothing to show: an untouched record
 * shouldn't grow an empty "history" panel.
 */
export function EditHistory({
  api,
  entity,
  entityId,
}: {
  api: ReturnType<typeof apiClient>;
  entity: RecordEdit["entity"];
  entityId: string | number;
}) {
  const query = useQuery({
    queryKey: ["admin", "record-edits", entity, String(entityId)],
    queryFn: () => api.adminRecordEdits(entity, entityId),
    staleTime: 30 * 1000,
    retry: 1,
  });

  const edits = query.data ?? [];
  if (edits.length === 0) return null;

  return (
    <div className="space-y-1.5 border-t border-border pt-4">
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted">
        <History className="h-3.5 w-3.5" aria-hidden />
        Corrections
      </div>
      <ul className="divide-y divide-border rounded-card border border-border bg-paper">
        {edits.map((edit) => (
          <li key={edit.id} className="px-3 py-2 text-sm">
            <div className="text-ink">
              <span className="font-medium">{editFieldLabel(edit.field)}</span>{" "}
              <span className="text-muted line-through">{edit.old_value ?? "blank"}</span>{" "}
              <span aria-hidden>→</span> <span>{edit.new_value ?? "blank"}</span>
            </div>
            <div className="text-xs text-muted">
              {formatWhen(edit.at)}
              {edit.actor ? ` · ${edit.actor}` : ""}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** "extra.emergency_contact_name" -> "Emergency Contact Name". */
export function editFieldLabel(field: string): string {
  return humanizeKey(field.startsWith("extra.") ? field.slice("extra.".length) : field);
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
